import {
  IUserProfileUpdate,
  USER_AVAILABLE_STATUS,
  IUserProfile,
} from "@home-health-notify/domain";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import React, { useContext, useEffect } from "react";
import CommonDialog from "../../common/CommonDialog/CommonDialog";
import InputPhoto from "./inputFields/InputPhoto";
import InputStatus from "./inputFields/InputStatus";
import UserProfileItem from "./UserProfileItem";
import { FormWrapper, UserProfileWrapper } from "./UserProfileModal.style";

interface IUserProfileModalProps {
  closeModal: () => void;
}

export const UserProfileModal = ({ closeModal }: IUserProfileModalProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "userProfile" });
  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const userProfileData: IUserProfile | undefined =
    selectedOrgUser?.getOrganization()?.userProfile;
  const userInfo = userProfileData
    ? (userDirectory?.getUserInfo(userProfileData.id) ?? userProfileData)
    : undefined;

  const [availableStatus, setAvailableStatus] =
    React.useState<USER_AVAILABLE_STATUS>(
      userInfo?.availableStatus ?? USER_AVAILABLE_STATUS.AVAILABLE,
    );

  useEffect(() => {
    if (userInfo?.availableStatus) {
      setAvailableStatus(userInfo.availableStatus);
    }
  }, [userInfo?.availableStatus]);

  const onUpdate = async (userProfileData: IUserProfileUpdate) => {
    if (selectedOrgUser) {
      return await selectedOrgUser.updateUserProfile(userProfileData);
    }
    return;
  };

  const onStatusChange = async (status: USER_AVAILABLE_STATUS) => {
    setAvailableStatus(status);
    const userProfileUpdate: IUserProfileUpdate = {
      availableStatus: status,
    };
    try {
      await onUpdate(userProfileUpdate);
      enqueueSnackbar(tNs("statusUpdated"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(tNs("statusUpdateFailed"), { variant: "error" });
    }
  };

  const onImageUpload = async (file: string) => {
    const userProfileUpdate: IUserProfileUpdate = {
      image: file,
    };
    try {
      await onUpdate(userProfileUpdate);
      enqueueSnackbar(tNs("imageUpdated"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(tNs("imageUpdateFailed"), { variant: "error" });
    }
  };
  return (
    <CommonDialog
      isDialogOpen={true}
      onDialogClose={closeModal}
      title={userInfo?.title || ""}
      maxWidth="sm"
    >
      <FormWrapper>
        <InputPhoto
          src={userInfo?.image || ""}
          name={userInfo?.title || ""}
          onImageUpload={onImageUpload}
        />
        <UserProfileWrapper>
          <UserProfileItem
            text={tNs("fullName")}
            value={`${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`}
          />
          <UserProfileItem
            text={tNs("organizationName")}
            value={selectedOrgUser?.getOrganization()?.organizationName ?? ""}
          />
          <UserProfileItem
            text={tNs("emailAddress")}
            value={userInfo?.email || ""}
          />
          <UserProfileItem
            text={tNs("phoneNumber")}
            value={`${userInfo?.countryCode || ""} ${userInfo?.phoneNo || ""}`}
          />

          <UserProfileItem text={tNs("about")} value={userInfo?.about || ""} />
          {userInfo?.department && (
            <UserProfileItem
              text={tNs("department")}
              value={userInfo.department.name || ""}
            />
          )}
          <InputStatus
            availableStatus={availableStatus}
            setAvailableStatus={onStatusChange}
          />
        </UserProfileWrapper>
      </FormWrapper>
    </CommonDialog>
  );
};
